import React, { useState, useEffect } from 'react';

// === PREMIUM DUMMY DATA ===
const healthPackages = [
  { id: 1, name: "Executive Health Check", price: "₹4,999", desc: "Comprehensive full-body screening including 60+ parameters, ECG, Chest X-Ray, and Physician Consultation.", icon: "🩺" },
  { id: 2, name: "Advanced Cardiac Care", price: "₹7,499", desc: "Targeted heart health assessment with 2D Echo, TMT, Lipid Profile, and Expert Cardiologist Review.", icon: "❤️" },
  { id: 3, name: "Women's Wellness Plus", price: "₹5,999", desc: "Designed for women's health featuring Mammography, Pap Smear, Thyroid Profile, and Gynecologist Consult.", icon: "🌸" },
  { id: 4, name: "Senior Citizen Care", price: "₹3,999", desc: "Preventive screening for elderly focusing on bone health, diabetes, and vision checkups.", icon: "👴" }
];

const specialists = [
  { name: 'Dr. Siddharth Rao', title: 'Director - Cardiology', dept: 'Cardiology', credentials: 'MD, DM, FACC (USA)', img: 'https://www.medijourney.co.in/uploads/51ba570fe68fc088e0a942bdf8700cdce7eb8b1e/1720502739Dr--Siddharth-Shirish-Dhande.jpg' },
  { name: 'Dr. Neha Kapoor', title: 'Head of Dermatology', dept: 'Dermatology', credentials: 'MD (Skin & VD), Fellow in Aesthetics', img: 'https://img.freepik.com/premium-photo/indian-female-doctor-hospital-medic-woman-professional-specialist-health-successful-young-lady_789795-545.jpg' },
  { name: 'Dr. Aman Verma', title: 'Chief Endodontist', dept: 'Dental Surgery', credentials: 'BDS, MDS (Gold Medalist)', img: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?q=80&w=400&auto=format&fit=crop' },
  { name: 'Dr. Sarah Thomas', title: 'Senior Pediatrician', dept: 'Pediatrics', credentials: 'MBBS, MD (Pediatrics)', img: 'https://www.dukehealth.org/sites/default/files/styles/doctor_profile/public/physician/sarah-p-thomas-md.jpg?itok=ioxXojBj' }
];

const specialtiesData = [
  { name: "Advanced Cardiology", icon: "🫀", desc: "State-of-the-art heart care including minimally invasive valve surgeries, angioplasty, and a 24/7 dedicated cath lab.", treatments: ["Robotic CABG", "TAVR", "Pacemaker Implantation"] },
  { name: "Neurology & Spine", icon: "🧠", desc: "Expert care for complex brain and spine disorders using high-precision microscopic and neuro-navigation surgeries.", treatments: ["Stroke Management", "Spinal Fusion", "Brain Tumor Excision"] },
  { name: "Orthopedics & Joint", icon: "🦴", desc: "Pioneers in 100% robotic joint replacement and advanced sports medicine for faster recovery.", treatments: ["Robotic Knee Replacement", "Arthroscopy", "Complex Trauma"] },
  { name: "Oncology (Cancer Care)", icon: "🎗️", desc: "Comprehensive cancer care with targeted therapy, precision radiation, and expert surgical oncology.", treatments: ["Chemotherapy", "TrueBeam Radiation", "Immunotherapy"] },
  { name: "Aesthetics & Plastic", icon: "✨", desc: "World-class cosmetic enhancements, reconstructive surgeries, and advanced laser treatments.", treatments: ["Rhinoplasty", "Hair Transplant", "Anti-aging Laser"] },
  { name: "Gastroenterology", icon: "🩺", desc: "Advanced endoscopic procedures, liver disease management, and minimally invasive GI surgeries.", treatments: ["ERCP / Endoscopy", "Liver Transplant", "Bariatric Surgery"] }
];

const facilities = [
  "NABL Accredited Pathology Lab", "24/7 Emergency & Trauma Care", "Advanced Digital Imaging (3D MRI/CT)", "Class 100 Modular Operation Theaters", "In-house 24x7 Pharmacy", "Premium Luxury Patient Suites"
];

// === COMPONENT ===
const ClinicPremium: React.FC = () => {
  type Page = 'home' | 'specialties' | 'packages' | 'doctors' | 'portal' | 'contact';
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Smart Booking Modal State
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [prefilledDept, setPrefilledDept] = useState('General Consult');
  const [bookingSuccess, setBookingSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  }, [currentPage]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const bookPackageViaWhatsApp = (pkgName: string, price: string) => {
    const message = `Hello Apex Premier, I would like to book the *${pkgName}* (${price}). Please let me know the available slots for this week.`;
    window.open(`https://wa.me/919876543210?text=${encodeURIComponent(message)}`, '_blank');
  };

  // The Magic Function: Opens modal with specific department selected
  const openSmartBooking = (department: string = "General Consult") => {
    setPrefilledDept(department);
    setIsBookingOpen(true);
    setBookingSuccess(false);
  };

  const handleBookingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setBookingSuccess(true);
    setTimeout(() => {
      setIsBookingOpen(false);
      setBookingSuccess(false);
    }, 4000);
  };

  // Reusable Hero Banner for Subpages
  const SubpageHero = ({ title, subtitle, bgImage }: { title: string, subtitle: string, bgImage: string }) => (
    <div className="relative h-[40vh] md:h-[50vh] flex items-center justify-center text-center px-6 overflow-hidden">
      <div className="absolute inset-0">
        <img src={bgImage} alt={title} className="w-full h-full object-cover opacity-40"/>
        <div className="absolute inset-0 bg-slate-950/80 mix-blend-multiply"></div>
      </div>
      <div className="relative z-10 max-w-4xl animate-slideUp">
        <h1 className="text-4xl md:text-6xl font-serif text-white font-medium mb-4 leading-tight">{title}</h1>
        <p className="text-slate-300 text-lg font-light tracking-wide">{subtitle}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans flex flex-col selection:bg-blue-200">
      
      {/* === SMART BOOKING MODAL (GLOBAL) === */}
      {isBookingOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-md" onClick={() => setIsBookingOpen(false)}></div>
          <div className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-slideUp flex flex-col md:flex-row">
            
            {/* Left Info Panel */}
            <div className="hidden md:flex md:w-2/5 bg-slate-900 p-8 flex-col justify-between relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/always-grey.png')]"></div>
              <div className="relative z-10">
                <span className="text-4xl text-blue-500 mb-4 block">✚</span>
                <h3 className="text-2xl font-serif text-white mb-2">Priority Booking</h3>
                <p className="text-slate-400 text-sm font-light">Your health is our utmost priority. Fast-track your consultation.</p>
              </div>
              <div className="relative z-10 space-y-4 text-xs font-bold text-slate-300 uppercase tracking-widest">
                <p>📍 New Delhi, India</p>
                <p>📞 +91 98765 43210</p>
              </div>
            </div>

            {/* Form Panel */}
            <div className="w-full md:w-3/5 p-8 relative">
              <button onClick={() => setIsBookingOpen(false)} className="absolute top-4 right-6 text-slate-400 hover:text-slate-800 text-3xl font-light">×</button>
              
              {bookingSuccess ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-10">
                  <div className="w-20 h-20 bg-green-50 text-green-600 rounded-full flex items-center justify-center text-4xl mb-2">✓</div>
                  <h3 className="text-2xl font-serif font-bold text-slate-900">Appointment Requested</h3>
                  <p className="text-slate-500 text-sm">Our concierge desk will contact you within 10 minutes to confirm your slot for <strong className="text-blue-700">{prefilledDept}</strong>.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-serif text-slate-900 mb-6 pr-8">Request Consultation</h3>
                  <form onSubmit={handleBookingSubmit} className="space-y-5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Patient Name</label>
                      <input required type="text" className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition text-sm" placeholder="Full Name" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Mobile Number</label>
                      <input required type="tel" className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition text-sm" placeholder="+91 00000 00000" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Department</label>
                        <select 
                          value={prefilledDept} 
                          onChange={(e) => setPrefilledDept(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-200 px-3 py-3 rounded-lg focus:outline-none focus:border-blue-500 transition text-sm text-slate-800"
                        >
                          <option value="General Consult">General Consult</option>
                          {specialtiesData.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                          <option value="Dental Surgery">Dental Surgery</option>
                          <option value="Pediatrics">Pediatrics</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Date</label>
                        <input required type="date" className="w-full bg-slate-50 border border-slate-200 px-3 py-3 rounded-lg focus:outline-none focus:border-blue-500 transition text-sm text-slate-800" />
                      </div>
                    </div>
                    <button type="submit" className="w-full bg-blue-700 text-white font-bold text-[11px] uppercase tracking-widest py-4 rounded-lg hover:bg-blue-800 transition shadow-lg mt-2">
                      Confirm Request
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- TOP BRANDING & EMERGENCY BAR --- */}
      <div className="bg-slate-950 text-white text-[10px] md:text-[11px] py-2.5 px-4 md:px-8 flex flex-col sm:flex-row justify-between items-center gap-2 tracking-widest uppercase font-semibold">
        <div className="flex gap-6">
          <span className="hidden md:inline">JCI & NABL Accredited Center</span>
          <span className="text-blue-400 cursor-pointer hover:text-white transition flex items-center gap-1" onClick={() => setCurrentPage('portal')}>
            <span className="text-sm">🔐</span> Patient Portal
          </span>
        </div>
        <div className="flex items-center gap-2 text-red-400 bg-red-950/30 px-3 py-1 rounded-full border border-red-900/50">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span> 24/7 Emergency Line: +91 99999 88888
        </div>
      </div>

      {/* --- LUXURY NAVBAR --- */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 border-b ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg border-slate-200 py-2' : 'bg-white border-slate-100 py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          
          <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setCurrentPage('home')}>
            <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-700 text-white flex items-center justify-center rounded-lg text-2xl font-bold shadow-md group-hover:bg-blue-800 transition">✚</div>
            <div className="leading-none">
              <span className="text-xl md:text-2xl font-serif text-slate-900 font-bold block">Apex Premier</span>
              <span className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-blue-700 font-bold">Medical Institute</span>
            </div>
          </div>
          
          <div className="hidden lg:flex gap-8 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
            {['home', 'specialties', 'packages', 'doctors', 'contact'].map(page => (
              <button 
                key={page} 
                onClick={() => setCurrentPage(page as Page)}
                className={`hover:text-blue-700 transition ${currentPage === page ? 'text-blue-700 border-b-2 border-blue-700 pb-1' : ''}`}
              >
                {page.replace('packages', 'health packages')}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button onClick={() => openSmartBooking('General Consult')} className="hidden sm:block bg-slate-900 text-white px-6 py-3 rounded-lg text-[11px] font-bold uppercase tracking-widest hover:bg-blue-700 transition shadow-lg">
              Book Appointment
            </button>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden text-blue-700 text-3xl leading-none">☰</button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-slate-200 shadow-2xl flex flex-col p-6 animate-fadeIn">
            {['home', 'specialties', 'packages', 'doctors', 'portal', 'contact'].map(page => (
              <button key={page} onClick={() => setCurrentPage(page as Page)} className="py-4 text-left text-xs font-bold uppercase tracking-widest border-b border-slate-100 text-slate-600">
                {page}
              </button>
            ))}
            <button onClick={() => openSmartBooking('General Consult')} className="mt-6 w-full bg-blue-700 text-white px-6 py-4 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-blue-800 transition">
              Book Appointment
            </button>
          </div>
        )}
      </nav>

      {/* --- PAGE CONTENT AREA --- */}
      <main className="flex-grow animate-fadeIn">
        
        {/* ================= 1. HOME PAGE (MASSIVE & PREMIUM) ================= */}
        {currentPage === 'home' && (
          <div className="animate-slideUp">
            
            {/* Enterprise Hero */}
            <header className="relative min-h-[85vh] flex items-center px-4 sm:px-6 md:px-12 lg:px-24 overflow-hidden bg-slate-900">
              <div className="absolute inset-0">
                <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=1600&auto=format&fit=crop" alt="Hospital" className="w-full h-full object-cover opacity-40 mix-blend-overlay"/>
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/80 to-transparent"></div>
              </div>
              <div className="relative z-10 max-w-3xl space-y-8 mt-10 md:mt-0">
                <span className="text-blue-400 font-bold tracking-[0.3em] uppercase text-xs border-l-4 border-blue-500 pl-3">Redefining Clinical Excellence</span>
                <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif text-white leading-[1.1] font-medium">
                  World-Class Care.<br/>Compassionate Healing.
                </h1>
                <p className="text-slate-300 text-lg font-light leading-relaxed max-w-xl">
                  Apex Premier brings together internationally trained medical experts, cutting-edge technology, and a patient-first approach to deliver healthcare that meets global standards.
                </p>
                <div className="pt-4 flex flex-col sm:flex-row gap-4">
                  <button onClick={() => openSmartBooking('General Consult')} className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold uppercase tracking-widest text-xs hover:bg-blue-700 transition shadow-xl">Book Consultation</button>
                  <button onClick={() => setCurrentPage('packages')} className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-lg font-bold uppercase tracking-widest text-xs hover:bg-white/20 transition">View Health Packages</button>
                </div>
              </div>
            </header>

            {/* Premium Stats / Trust Strip */}
            <div className="bg-white border-b border-slate-200 relative z-20 shadow-xl -mt-10 mx-4 md:mx-12 lg:mx-24 rounded-2xl flex flex-wrap justify-between p-8 md:p-12 gap-8">
               <div className="flex items-center gap-5">
                 <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 text-2xl">🏆</div>
                 <div><h4 className="font-bold text-slate-900 text-2xl font-serif">No. 1</h4><p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Ranked Hospital</p></div>
               </div>
               <div className="flex items-center gap-5">
                 <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 text-2xl">👨‍⚕️</div>
                 <div><h4 className="font-bold text-slate-900 text-2xl font-serif">250+</h4><p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Expert Doctors</p></div>
               </div>
               <div className="flex items-center gap-5">
                 <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 text-2xl">🏥</div>
                 <div><h4 className="font-bold text-slate-900 text-2xl font-serif">500+</h4><p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Bed Capacity</p></div>
               </div>
               <div className="flex items-center gap-5">
                 <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 text-2xl">🚑</div>
                 <div><h4 className="font-bold text-slate-900 text-2xl font-serif">24/7</h4><p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Emergency Care</p></div>
               </div>
            </div>

            {/* NEW: Technology & Innovation (Robotic Surgery) */}
            <section className="py-32 px-6 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">Innovation in Healing</span>
                <h2 className="text-4xl md:text-5xl font-serif text-slate-900 font-medium leading-tight">Pioneering Robotic & Minimally Invasive Surgery.</h2>
                <p className="text-slate-600 text-lg leading-relaxed font-light">We are the first institute in the region to introduce the 4th Generation Da Vinci Robotic Surgical System. This means smaller incisions, less pain, zero blood loss, and a recovery time that is 3x faster than traditional surgeries.</p>
                <div className="grid grid-cols-2 gap-6 pt-4">
                  <div className="border-l-2 border-blue-600 pl-4">
                    <h4 className="font-bold text-slate-900 text-xl">100%</h4>
                    <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">Precision</p>
                  </div>
                  <div className="border-l-2 border-blue-600 pl-4">
                    <h4 className="font-bold text-slate-900 text-xl">24 Hrs</h4>
                    <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">Discharge Time</p>
                  </div>
                </div>
                <button onClick={() => setCurrentPage('specialties')} className="mt-4 border-b-2 border-blue-600 text-blue-700 font-bold uppercase tracking-widest text-xs pb-1 hover:text-slate-900 transition">View Surgical Departments ➔</button>
              </div>
              <div className="relative h-[500px] rounded-[2.5rem] overflow-hidden shadow-2xl border border-slate-200">
                <img src="https://www.orthopaedicweblinks.com/wp-content/uploads/2024/08/27499-robotic-surgery-the-next-frontier-in-orthopedic-procedures.jpg" alt="Robotic Surgery" className="w-full h-full object-cover"/>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-8">
                  <p className="text-white font-serif text-2xl">Advanced Orthopedic Robotics</p>
                </div>
              </div>
            </section>

            {/* Health Packages Highlight */}
            <section className="py-24 px-4 sm:px-6 bg-slate-50 border-y border-slate-200">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                  <div>
                    <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">Preventive Care</span>
                    <h2 className="text-4xl md:text-5xl font-serif text-slate-900 mt-2 font-medium">Premium Health Packages</h2>
                  </div>
                  <button onClick={() => setCurrentPage('packages')} className="text-slate-500 hover:text-blue-700 uppercase tracking-widest text-xs font-bold transition bg-white px-6 py-3 rounded-lg border border-slate-200 shadow-sm">View All Packages</button>
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                  {healthPackages.slice(0, 3).map((pkg) => (
                    <div key={pkg.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 overflow-hidden flex flex-col">
                      <div className="bg-blue-50/50 p-8 border-b border-slate-100 flex justify-between items-start">
                        <span className="text-5xl">{pkg.icon}</span>
                      </div>
                      <div className="p-8 flex-grow flex flex-col">
                        <h3 className="text-2xl font-serif font-bold text-slate-900 mb-3">{pkg.name}</h3>
                        <p className="text-slate-500 text-sm leading-relaxed mb-8 flex-grow">{pkg.desc}</p>
                        <div className="flex justify-between items-center mt-auto">
                          <span className="text-2xl font-bold text-blue-700">{pkg.price}</span>
                          <button onClick={() => bookPackageViaWhatsApp(pkg.name, pkg.price)} className="bg-slate-900 text-white px-5 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-blue-600 transition">Book ➔</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* NEW: International Patients (Medical Tourism) */}
            <section className="py-32 px-6 bg-slate-900 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-900/20 rounded-full blur-3xl"></div>
              <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center relative z-10">
                <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
                  <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=400&auto=format&fit=crop" className="w-full h-64 object-cover rounded-2xl" alt="Airplane"/>
                  <img src="https://streamline.imgix.net/b13da479-fe57-4777-9377-7510b119d4c9/fa729eca-8439-4039-ba7d-d302e30f946a/Patient-room-1.jpg?ixlib=rb-1.1.0&w=2000&h=2000&fit=max&or=0&s=98d72b62cf45f32343ad2110d420f5c1" className="w-full h-64 object-cover rounded-2xl mt-8" alt="Hospital Room"/>
                </div>
                <div className="order-1 lg:order-2 space-y-8">
                  <span className="text-blue-400 font-bold uppercase tracking-[0.2em] text-xs border-l-4 border-blue-500 pl-3">Global Healing</span>
                  <h2 className="text-4xl md:text-5xl font-serif font-medium leading-tight">International Patient Services.</h2>
                  <p className="text-slate-400 text-lg font-light leading-relaxed">We welcome patients from over 50 countries. Our dedicated international desk handles everything from medical visas and airport transfers to language translators and premium accommodation.</p>
                  <ul className="space-y-4 pt-4">
                    <li className="flex items-center gap-4 text-sm font-bold tracking-wide"><span className="text-blue-500 text-xl">✓</span> Complimentary Airport Pick & Drop</li>
                    <li className="flex items-center gap-4 text-sm font-bold tracking-wide"><span className="text-blue-500 text-xl">✓</span> Dedicated Relationship Manager</li>
                    <li className="flex items-center gap-4 text-sm font-bold tracking-wide"><span className="text-blue-500 text-xl">✓</span> Multi-cuisine In-house Dining</li>
                  </ul>
                  <button onClick={() => setCurrentPage('contact')} className="mt-8 border border-slate-600 text-white px-8 py-4 rounded-lg font-bold uppercase tracking-widest text-xs hover:bg-white hover:text-slate-900 transition">Contact International Desk</button>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* ================= 2. SPECIALTIES PAGE (NEW & LUXURIOUS) ================= */}
        {currentPage === 'specialties' && (
          <div className="animate-slideUp pb-32 bg-white">
            <SubpageHero title="Centers of Excellence" subtitle="Advanced clinical departments led by global experts." bgImage="https://images.unsplash.com/photo-1551076805-e1869043e560?q=80&w=1600&auto=format&fit=crop" />
            
            <div className="max-w-7xl mx-auto px-6 mt-24">
              <div className="grid md:grid-cols-2 gap-12">
                {specialtiesData.map((dept, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-[2rem] p-10 border border-slate-100 shadow-sm flex flex-col h-full group hover:bg-white hover:shadow-2xl transition duration-500">
                    <div className="flex items-center gap-6 mb-6 border-b border-slate-200 pb-6">
                      <div className="w-20 h-20 bg-blue-100 text-blue-700 rounded-2xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">{dept.icon}</div>
                      <h3 className="text-3xl font-serif font-bold text-slate-900">{dept.name}</h3>
                    </div>
                    <p className="text-slate-600 leading-relaxed mb-8">{dept.desc}</p>
                    
                    <div className="mb-8 flex-grow">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Key Treatments</h4>
                      <div className="flex flex-wrap gap-2">
                        {dept.treatments.map((t, i) => (
                          <span key={i} className="bg-slate-200/50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold">{t}</span>
                        ))}
                      </div>
                    </div>

                    <button 
                      onClick={() => openSmartBooking(dept.name)}
                      className="w-full bg-white border-2 border-slate-200 text-slate-800 py-4 rounded-xl font-bold uppercase tracking-widest text-xs hover:border-blue-600 hover:text-blue-700 transition flex items-center justify-center gap-3"
                    >
                      Book {dept.name.split(' ')[0]} Consult <span>➔</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= 3. HEALTH PACKAGES PAGE ================= */}
        {currentPage === 'packages' && (
          <div className="animate-slideUp bg-slate-50 min-h-screen pb-32">
            <SubpageHero title="Preventive Health Packages" subtitle="Proactive care tailored for your lifestyle and age." bgImage="https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=1600&auto=format&fit=crop" />
            
            <div className="max-w-7xl mx-auto px-6 mt-24">
              <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-10">
                {healthPackages.map((pkg) => (
                  <div key={pkg.id} className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200 shadow-sm flex flex-col md:flex-row gap-8 items-center md:items-start group hover:border-blue-200 transition">
                    <div className="w-24 h-24 bg-blue-50 rounded-2xl flex items-center justify-center text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">{pkg.icon}</div>
                    <div className="flex-grow text-center md:text-left">
                      <h3 className="text-2xl font-serif font-bold text-slate-900 mb-3">{pkg.name}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed mb-6">{pkg.desc}</p>
                      <div className="flex flex-col sm:flex-row items-center gap-6 justify-between border-t border-slate-100 pt-6">
                        <span className="text-3xl font-bold text-blue-700">{pkg.price}</span>
                        <button 
                          onClick={() => bookPackageViaWhatsApp(pkg.name, pkg.price)}
                          className="w-full sm:w-auto bg-slate-900 text-white px-8 py-3 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-blue-600 transition"
                        >
                          Book via WhatsApp
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Corporate Packages Banner */}
              <div className="mt-20 bg-blue-700 text-white rounded-3xl p-12 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className="relative z-10 max-w-2xl space-y-4">
                  <h3 className="text-3xl font-serif font-medium">Corporate Wellness Programs</h3>
                  <p className="text-blue-100 font-light">We offer customized annual health checkup packages for organizations. Ensure the well-being of your workforce with Apex Premier.</p>
                </div>
                <button onClick={() => setCurrentPage('contact')} className="relative z-10 bg-white text-blue-800 px-8 py-4 rounded-lg font-bold uppercase tracking-widest text-xs hover:bg-slate-100 transition whitespace-nowrap">Contact Sales</button>
              </div>
            </div>
          </div>
        )}

        {/* ================= 4. DOCTORS PAGE ================= */}
        {currentPage === 'doctors' && (
          <div className="animate-slideUp pb-32">
            <SubpageHero title="Our Medical Board" subtitle="Leaders in medicine, pioneers in research, partners in healing." bgImage="https://images.unsplash.com/photo-1584982751601-e1570d98ac15?q=80&w=1600&auto=format&fit=crop" />
            
            <div className="max-w-7xl mx-auto px-6 mt-24">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {specialists.map((doc, idx) => (
                  <div key={idx} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-2xl transition duration-500 group flex flex-col">
                    <div className="h-80 overflow-hidden bg-slate-100 relative">
                      <img src={doc.img} alt={doc.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700 grayscale group-hover:grayscale-0"/>
                      <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest text-slate-800 shadow-sm">{doc.dept}</div>
                    </div>
                    <div className="p-6 text-center flex-grow flex flex-col">
                      <h3 className="text-xl font-bold text-slate-900 mb-1">{doc.name}</h3>
                      <p className="text-blue-600 font-bold uppercase tracking-widest text-[10px] mb-2">{doc.title}</p>
                      <p className="text-slate-500 text-xs mb-6 flex-grow">{doc.credentials}</p>
                      <button 
                        onClick={() => openSmartBooking(doc.dept)} 
                        className="mt-auto w-full border border-slate-200 text-slate-700 py-3 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-blue-600 hover:text-white hover:border-blue-600 transition"
                      >
                        Book Consult
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= 5. PATIENT PORTAL (DUMMY UI) ================= */}
        {currentPage === 'portal' && (
          <div className="animate-slideUp min-h-screen bg-slate-50 flex items-center justify-center py-20 px-6 relative">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100 rounded-full blur-3xl opacity-50"></div>
            <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden relative z-10">
              <div className="bg-blue-700 p-8 text-center text-white">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-3xl mx-auto mb-4">🔐</div>
                <h2 className="text-2xl font-serif font-bold">ApexCare Portal</h2>
                <p className="text-blue-100 text-sm mt-2 font-light">Access your digital health records securely.</p>
              </div>
              <div className="p-8">
                <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); alert('Demo Login Successful!'); }}>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">UHID / Patient ID</label>
                    <input type="text" required className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500 transition text-sm" placeholder="APX-00000" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Registered Mobile</label>
                    <input type="tel" required className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-blue-500 transition text-sm" placeholder="+91" />
                  </div>
                  <button type="submit" className="w-full bg-slate-900 text-white font-bold text-xs uppercase tracking-widest py-4 rounded-lg hover:bg-blue-600 transition shadow-lg">Request OTP</button>
                </form>
                
                <div className="mt-8 pt-6 border-t border-slate-100 space-y-4 text-center">
                  <p className="text-sm font-bold text-slate-700">What can you do here?</p>
                  <div className="flex justify-center gap-6 text-2xl text-blue-600">
                    <span title="Download Lab Reports" className="cursor-pointer hover:scale-110 transition">📄</span>
                    <span title="View Prescriptions" className="cursor-pointer hover:scale-110 transition">💊</span>
                    <span title="Manage Appointments" className="cursor-pointer hover:scale-110 transition">📅</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= 6. CONTACT PAGE ================= */}
        {currentPage === 'contact' && (
          <div className="animate-slideUp bg-white py-24 px-6 relative">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16">
              
              <div className="lg:col-span-5 space-y-10 flex flex-col justify-center">
                <div>
                  <h1 className="text-5xl font-serif text-slate-900 mb-4 font-medium">Get in Touch</h1>
                  <p className="text-slate-500 text-lg leading-relaxed font-light">Need to book a consultation, request medical records, or inquire about international admissions? Our concierge team is ready to assist you 24/7.</p>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm">
                    <span className="text-3xl text-blue-600">📍</span>
                    <div><h4 className="font-bold text-slate-900 uppercase tracking-widest text-xs mb-2">The Apex Campus</h4><p className="text-slate-600 text-sm">101, Luxury Healthcare Avenue,<br/>New Delhi, India 110001</p></div>
                  </div>
                  <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm">
                    <span className="text-3xl text-blue-600">📞</span>
                    <div><h4 className="font-bold text-slate-900 uppercase tracking-widest text-xs mb-2">Concierge & Emergency</h4><p className="text-slate-600 text-sm">Reception: +91 98765 43210<br/><span className="text-red-600 font-bold mt-1 block">Trauma Center: +91 99999 88888</span></p></div>
                  </div>
                  <button onClick={() => openSmartBooking('General Consult')} className="w-full bg-slate-900 text-white font-bold text-xs uppercase tracking-widest py-4 rounded-xl hover:bg-blue-600 transition shadow-lg">
                    Open Smart Booking Form
                  </button>
                </div>
              </div>

              <div className="lg:col-span-7 h-[600px] bg-slate-200 rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white">
                 <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14008.114827184043!2d77.21595625!3d28.6295706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd37b741d057%3A0xc46188cbcc01452!2sConnaught%20Place%2C%20New%20Delhi%2C%20Delhi%20110001!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin" className="w-full h-full grayscale hover:grayscale-0 transition duration-700"></iframe>
              </div>

            </div>
          </div>
        )}

      </main>

      {/* --- MEGA LUXURY FOOTER --- */}
      <footer className="bg-slate-950 text-slate-400 py-20 px-6 border-t-[8px] border-blue-700 mt-auto">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16">
          
          <div className="lg:col-span-5 space-y-6">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
              <span className="w-10 h-10 bg-blue-700 text-white flex items-center justify-center rounded-lg text-xl font-bold">✚</span>
              <div className="leading-none">
                <span className="text-2xl font-serif text-white font-bold block">Apex Premier</span>
                <span className="text-[9px] uppercase tracking-[0.2em] text-blue-500 font-bold">Medical Institute</span>
              </div>
            </div>
            <p className="text-sm font-light leading-relaxed max-w-sm">Global standards of healthcare, advanced medical technology, and a dedicated team of world-class specialists committed to your healing journey.</p>
            <div className="flex gap-4 pt-2">
              <span className="w-10 h-10 border border-slate-800 rounded-full flex items-center justify-center text-xs font-bold hover:bg-blue-700 hover:text-white transition cursor-pointer">IN</span>
              <span className="w-10 h-10 border border-slate-800 rounded-full flex items-center justify-center text-xs font-bold hover:bg-blue-700 hover:text-white transition cursor-pointer">FB</span>
              <span className="w-10 h-10 border border-slate-800 rounded-full flex items-center justify-center text-xs font-bold hover:bg-blue-700 hover:text-white transition cursor-pointer">X</span>
            </div>
          </div>
          
          <div className="lg:col-span-3 space-y-6">
             <h4 className="font-bold text-white uppercase tracking-[0.2em] text-xs">Patient Care</h4>
             <ul className="space-y-4 text-sm font-light">
                <li><button onClick={() => openSmartBooking('General Consult')} className="hover:text-blue-400 transition">Book Appointment</button></li>
                <li><button onClick={() => setCurrentPage('packages')} className="hover:text-blue-400 transition">Health Packages</button></li>
                <li><button onClick={() => setCurrentPage('portal')} className="hover:text-blue-400 transition">Patient Portal Login</button></li>
                <li><button onClick={() => setCurrentPage('doctors')} className="hover:text-blue-400 transition">Find a Doctor</button></li>
             </ul>
          </div>

          <div className="lg:col-span-4 space-y-6">
             <h4 className="font-bold text-white uppercase tracking-[0.2em] text-xs">Accreditations</h4>
             <div className="flex gap-6 mb-6">
                <div className="w-16 h-16 bg-slate-900 border border-slate-800 rounded flex items-center justify-center font-serif font-bold text-white text-xl">JCI</div>
                <div className="w-16 h-16 bg-slate-900 border border-slate-800 rounded flex items-center justify-center font-serif font-bold text-white text-xl">NABH</div>
             </div>
             <p className="text-xs font-light text-slate-500">Recognized globally for our commitment to clinical quality, advanced robotic surgeries, and patient safety standards.</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto border-t border-slate-900 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-600 text-center md:text-left">
           <p>© {new Date().getFullYear()} Apex Premier Medical Institute. All Rights Reserved.</p>
           <p>Developed for Premium Portfolio Demos</p>
        </div>
      </footer>

      {/* Global Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-slideUp { animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        .color-scheme-dark { color-scheme: dark; }
      `}} />
    </div>
  );
};

export default ClinicPremium;